import React, { useState, useEffect, useCallback, useRef, forwardRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    FaHouseChimney,
    FaFileInvoiceDollar,
    FaPeopleGroup,
    FaChartPie,
    FaRegChartBar,
    FaChevronRight,
    FaChevronDown,
} from 'react-icons/fa6';

// Interface for a menu item.
export interface IItem {
    title: string;
    icon: JSX.Element;
    link?: string;
    subMenu?: IItem[];
}

// Helper function to find the active path
const findActivePath = (items: IItem[], pathname: string): string[] | null => {
    for (const item of items) {
        if (item.link === pathname) {
            return [item.title];
        }
        if (item.subMenu) {
            const subPath = findActivePath(item.subMenu, pathname);
            if (subPath) {
                return [item.title, ...subPath];
            }
        }
    }
    return null;
};

// Corrected MenuItem Component
const MenuItem = React.memo(
    forwardRef<HTMLAnchorElement | HTMLButtonElement, {
        item: IItem;
        isOpen: boolean;
        isSelected: boolean;
        level: number;
        onClick: (item: IItem) => void;
    }>(({ item, isOpen, isSelected, level, onClick }, ref) => {

        const commonClasses = `flex items-center w-full px-4 py-2 my-1 mx-2 rounded-lg cursor-pointer transition-colors text-left ${
            isSelected
                ? 'bg-indigo-600 text-white shadow-lg'
                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
        }`;

        const style = { paddingLeft: `${1 + level * 1.5}rem` };

        if (item.subMenu) {
            return (
                <button
                    ref={ref as React.ForwardedRef<HTMLButtonElement>}
                    type="button"
                    className={commonClasses}
                    onClick={() => onClick(item)}
                    style={style}
                    aria-expanded={isOpen}
                >
                    <span className="mr-3 w-5 text-center">{item.icon}</span>
                    <span className="flex-1 font-medium">{item.title}</span>
                    <span className="ml-auto transition-transform duration-300">
            {isOpen ? <FaChevronDown size={14} /> : <FaChevronRight size={14} />}
          </span>
                </button>
            );
        }

        return (
            <a
                ref={ref as React.ForwardedRef<HTMLAnchorElement>}
                href={item.link}
                className={commonClasses}
                style={style}
                onClick={(e) => {
                    e.preventDefault();
                    onClick(item);
                }}
                aria-current={isSelected ? 'page' : undefined}
            >
                <span className="mr-3 w-5 text-center">{item.icon}</span>
                <span className="flex-1 font-medium">{item.title}</span>
            </a>
        );
    })
);

// Main Sidebar Component
export const Sidebar = ({ sidebarOpen, menuItems }: { sidebarOpen: boolean; menuItems: IItem[] }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [openMenus, setOpenMenus] = useState<Set<string>>(new Set());
    const activeItemRef = useRef<HTMLAnchorElement | HTMLButtonElement>(null);

    useEffect(() => {
        const activePath = findActivePath(menuItems, location.pathname);
        setOpenMenus(new Set(activePath || []));
    }, [location.pathname, menuItems]);

    useEffect(() => {
        activeItemRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });
    }, [location.pathname]);

    const handleMenuClick = useCallback(
        (item: IItem) => {
            if (item.link) {
                navigate(item.link);
            } else if (item.subMenu) {
                const newOpenMenus = new Set(openMenus);
                if (newOpenMenus.has(item.title)) {
                    newOpenMenus.delete(item.title);
                } else {
                    newOpenMenus.add(item.title);
                }
                setOpenMenus(newOpenMenus);
            }
        },
        [navigate, openMenus]
    );

    // This recursive function correctly renders the menu structure
    const renderMenuItems = (items: IItem[], level = 0) => {
        return items.map((item) => {
            const isSelected = location.pathname === item.link;
            const isOpen = openMenus.has(item.title);

            return (
                <div key={item.title}>
                    <MenuItem
                        ref={isSelected ? activeItemRef : null}
                        item={item}
                        isOpen={isOpen}
                        isSelected={isSelected}
                        level={level}
                        onClick={handleMenuClick}
                    />
                    {/* Sub-menu is rendered here, based on the parent's state */}
                    {isOpen && item.subMenu && (
                        <div className="mt-1">
                            {renderMenuItems(item.subMenu, level + 1)}
                        </div>
                    )}
                </div>
            );
        });
    };

    return (
        <aside
            className={`fixed inset-y-0 left-0 z-30 w-72 overflow-y-auto bg-gray-900 transition-transform duration-300 transform lg:translate-x-0 lg:static lg:inset-0 ${
                sidebarOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
        >
            <div className="flex items-center justify-center mt-8">
                <img src="/Syncroniza_transparente.png" alt="Logo" className="w-24 h-24" />
            </div>

            <nav className="mt-10">{renderMenuItems(menuItems)}</nav>
        </aside>
    );
};

export default Sidebar;